import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2, User, Box, Layers, Database, ExternalLink, Play,
} from 'lucide-react';

/**
 * GovernanceGraphFull — full-viewport, dark-theme variant of GovernanceGraph.
 * Same edges/nodes/animations; tuned for a maximised dark canvas inside the
 * Command Center modal. Wider viewBox + spread node positions per v7 brief.
 */

const NODE_KIND_ICON = {
  account:   Building2,
  identity:  User,
  container: Box,
  service:   Layers,
  data:      Database,
};

const TONE = {
  critical: { stroke: '#F87171', fill: '#7F1D1D', icon: '#FCA5A5', glow: '0 0 14px rgba(248,113,113,0.45)' },
  warning:  { stroke: '#FBBF24', fill: '#78350F', icon: '#FCD34D', glow: '0 0 14px rgba(251,191,36,0.40)' },
  lens:     { stroke: '#5EEAD4', fill: '#134E4A', icon: '#5EEAD4', glow: '0 0 14px rgba(94,234,212,0.30)' },
  neutral:  { stroke: '#64748B', fill: '#1E293B', icon: '#94A3B8', glow: 'none' },
};

const NODES_FULL = [
  { id: 'dev-sandbox',    label: 'stratum-dev-sandbox',          sublabel: 'DEV ACCOUNT',                    kind: 'account',   tone: 'neutral',  x: 60,  y: 320, w: 200 },
  { id: 'developer-role', label: 'developer-console-access',     sublabel: 'IAM Identity · Dev',             kind: 'identity',  tone: 'critical', x: 60,  y: 160, w: 200 },
  { id: 'ecr-image',      label: 'stratum/payments-api:latest',  sublabel: 'ECR · Dev origin · EOL',         kind: 'container', tone: 'critical', x: 340, y: 240, w: 210 },
  { id: 'lambda-role',    label: 'lambda-payments-processor',    sublabel: 'IAM Role · 1.4% effective',      kind: 'identity',  tone: 'warning',  x: 620, y: 160, w: 210 },
  { id: 'payments-prod',  label: 'stratum-payments-prod',        sublabel: 'PROD ACCOUNT · PCI scope',       kind: 'account',   tone: 'lens',     x: 620, y: 320, w: 210 },
  { id: 'fargate-task',   label: 'stratum-payments-api',         sublabel: 'Fargate · 184d · dev image',     kind: 'service',   tone: 'warning',  x: 620, y: 460, w: 200 },
  { id: 'pci-bucket',     label: 'stratum-pci-customer-data',    sublabel: 'S3 · Macie: Highly Confidential',kind: 'data',      tone: 'critical', x: 920, y: 360, w: 220 },
];

const EDGES = [
  { id: 'trust-path',   source: 'dev-sandbox',    target: 'payments-prod', label: 'IAM trust · 201 days · 2 traversals', tone: 'critical', dashed: true,  animated: true,  evt: 'evt-2025-10-03-trust' },
  { id: 'image-push',   source: 'developer-role', target: 'ecr-image',     label: 'Pushed Nov 28 · mutable :latest',     tone: 'warning',  dashed: true,  animated: false, evt: 'evt-2025-11-28-push' },
  { id: 'image-deploy', source: 'ecr-image',      target: 'fargate-task',  label: 'Deployed Feb 14 · no pipeline event', tone: 'critical', dashed: false, animated: false, evt: 'evt-2026-02-14-deploy' },
  { id: 'role-attach',  source: 'lambda-role',    target: 'fargate-task',  label: '847 granted · 12 used',               tone: 'warning',  dashed: false, animated: false, evt: 'iam-role-attach' },
  { id: 'data-access',  source: 'fargate-task',   target: 'pci-bucket',    label: '3 GetObject · during traversal',      tone: 'critical', dashed: false, animated: true,  evt: 'evt-2026-04-14-getobject' },
  { id: 'assume-role',  source: 'dev-sandbox',    target: 'lambda-role',   label: 'AssumeRole · Apr 14 02:17 UTC',       tone: 'critical', dashed: true,  animated: true,  evt: 'evt-2026-04-14-assumerole' },
];

const NODE_H = 64;
const VIEW_W = 1200;
const VIEW_H = 560;

// Temporal phase data — drives the slider/replay animation.
const PHASES = [
  {
    id: 0, date: 'Sep 26, 2025', label: 'Baseline',
    sub: 'Clean governance state · All controls active',
    nodes: { 'dev-sandbox': 'neutral', 'developer-role': 'neutral', 'ecr-image': 'neutral', 'lambda-role': 'neutral', 'payments-prod': 'lens', 'fargate-task': 'neutral', 'pci-bucket': 'lens' },
    edges: { 'trust-path': 'hidden', 'image-push': 'hidden', 'image-deploy': 'hidden', 'role-attach': 'forming', 'data-access': 'hidden', 'assume-role': 'hidden' },
  },
  {
    id: 1, date: 'Oct 3, 2025', label: 'Trust path created',
    sub: 'Unapproved IAM trust · no change ticket · no baseline',
    nodes: { 'dev-sandbox': 'warning', 'developer-role': 'neutral', 'ecr-image': 'neutral', 'lambda-role': 'neutral', 'payments-prod': 'warning', 'fargate-task': 'neutral', 'pci-bucket': 'lens' },
    edges: { 'trust-path': 'forming', 'image-push': 'hidden', 'image-deploy': 'hidden', 'role-attach': 'forming', 'data-access': 'hidden', 'assume-role': 'hidden' },
  },
  {
    id: 2, date: 'Nov 28, 2025', label: 'Dev image pushed',
    sub: 'developer-console-access pushed to shared ECR · no pipeline',
    nodes: { 'dev-sandbox': 'warning', 'developer-role': 'warning', 'ecr-image': 'warning', 'lambda-role': 'neutral', 'payments-prod': 'warning', 'fargate-task': 'neutral', 'pci-bucket': 'lens' },
    edges: { 'trust-path': 'forming', 'image-push': 'forming', 'image-deploy': 'hidden', 'role-attach': 'forming', 'data-access': 'hidden', 'assume-role': 'hidden' },
  },
  {
    id: 3, date: 'Feb 14, 2026', label: 'Dev image deployed to production',
    sub: 'No Terraform plan · no PR · no ServiceNow ticket',
    nodes: { 'dev-sandbox': 'warning', 'developer-role': 'warning', 'ecr-image': 'warning', 'lambda-role': 'warning', 'payments-prod': 'critical', 'fargate-task': 'critical', 'pci-bucket': 'warning' },
    edges: { 'trust-path': 'forming', 'image-push': 'active', 'image-deploy': 'active', 'role-attach': 'active', 'data-access': 'forming', 'assume-role': 'hidden' },
  },
  {
    id: 4, date: 'Apr 14, 2026 · 02:17 UTC', label: 'Trust path traversed · PCI data accessed',
    sub: 'AssumeRole succeeded · outside business hours · 3 GetObject on PCI data',
    nodes: { 'dev-sandbox': 'critical', 'developer-role': 'critical', 'ecr-image': 'warning', 'lambda-role': 'critical', 'payments-prod': 'critical', 'fargate-task': 'critical', 'pci-bucket': 'critical' },
    edges: { 'trust-path': 'traversed', 'image-push': 'active', 'image-deploy': 'active', 'role-attach': 'active', 'data-access': 'traversed', 'assume-role': 'traversed' },
  },
];

const EDGE_STATE_STYLE = {
  hidden:    { opacity: 0,    dashed: false, animated: false },
  forming:   { opacity: 0.55, dashed: true,  animated: false },
  active:    { opacity: 0.85, dashed: false, animated: false },
  traversed: { opacity: 1,    dashed: false, animated: true  },
};

const NODE_ROUTES = {
  'dev-sandbox':    '/lens/accounts/stratum-dev-sandbox',
  'developer-role': '/lens/identity?open=developer-console-access',
  'ecr-image':      '/lens/containers',
  'lambda-role':    '/lens/identity?open=lambda-payments-processor',
  'payments-prod':  '/lens/accounts/stratum-payments-prod',
  'fargate-task':   '/lens/containers',
  'pci-bucket':     '/lens/evidence',
};

function nodeCenter(n) { return { x: n.x + n.w / 2, y: n.y + NODE_H / 2 }; }
function bezierPath(s, t) {
  const dx = (t.x - s.x) * 0.5;
  return `M ${s.x},${s.y} C ${s.x + dx},${s.y} ${t.x - dx},${t.y} ${t.x},${t.y}`;
}

export default function GovernanceGraphFull() {
  const navigate = useNavigate();
  const [hoverNode, setHoverNode] = useState(null);
  const [hoverEdge, setHoverEdge] = useState(null);
  const [phase, setPhase] = useState(PHASES.length - 1); // start at full breach state
  const replayRef = useRef(null);
  const currentPhase = PHASES[phase];

  useEffect(() => () => { if (replayRef.current) clearInterval(replayRef.current); }, []);

  // Keyboard navigation: ←/→ change phase, space replays
  useEffect(() => {
    const handleKey = (e) => {
      const target = e.target;
      // Don't hijack arrows when user is typing in an input/textarea
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); jumpToPhase(Math.min(phase + 1, PHASES.length - 1)); }
      else if (e.key === 'ArrowLeft')  { e.preventDefault(); jumpToPhase(Math.max(phase - 1, 0)); }
      else if (e.key === ' ' || e.code === 'Space') { e.preventDefault(); replayTimeline(); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const replayTimeline = () => {
    if (replayRef.current) clearInterval(replayRef.current);
    setPhase(0);
    let i = 0;
    replayRef.current = setInterval(() => {
      i++;
      setPhase(i);
      if (i >= PHASES.length - 1) { clearInterval(replayRef.current); replayRef.current = null; }
    }, 1400);
  };

  const jumpToPhase = (i) => {
    if (replayRef.current) { clearInterval(replayRef.current); replayRef.current = null; }
    setPhase(i);
  };

  const connectedFromHover = useMemo(() => {
    if (!hoverNode) return null;
    return new Set(EDGES.filter((e) => e.source === hoverNode || e.target === hoverNode).map((e) => e.id));
  }, [hoverNode]);

  const nodeMap = useMemo(() => Object.fromEntries(NODES_FULL.map((n) => [n.id, n])), []);

  return (
    <div className="w-full h-full flex flex-col" data-testid="governance-graph-full">
      {/* Legend on top */}
      <div className="hidden md:flex flex-wrap items-center gap-5 text-[10px] font-mono text-slate-400 mb-3 shrink-0" data-testid="graph-full-legend">
        <span className="flex items-center gap-1.5">
          <svg width="20" height="2"><line x1="0" y1="1" x2="20" y2="1" stroke="#F87171" strokeWidth="2" strokeDasharray="4 3" /></svg>
          Unapproved path
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="20" height="2"><line x1="0" y1="1" x2="20" y2="1" stroke="#FBBF24" strokeWidth="2" /></svg>
          Governance finding
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="20" height="2"><line x1="0" y1="1" x2="20" y2="1" stroke="#F87171" strokeWidth="2" /></svg>
          Active traversal
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm" style={{ background: 'rgba(220,38,38,0.25)' }} />
          Blast radius spread
        </span>
      </div>

      {/* Desktop SVG graph — scaled to fill flex container */}
      <div className="hidden md:flex relative w-full flex-1 min-h-0 items-center justify-center">
        <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className="w-full h-full max-h-full" preserveAspectRatio="xMidYMid meet">
          <defs>
            <marker id="arrow-critical-full" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L0,6 L9,3 z" fill="#F87171" />
            </marker>
            <marker id="arrow-warning-full" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L0,6 L9,3 z" fill="#FBBF24" />
            </marker>
            <pattern id="dark-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1E293B" strokeWidth="0.5" />
            </pattern>
            {/* Contamination radial gradient — expands with phase */}
            <radialGradient id="contamination-spread" cx="15%" cy="55%" r="55%">
              <stop offset="0%"   stopColor="#DC2626" stopOpacity={phase >= 3 ? 0.18 : phase >= 2 ? 0.10 : 0} />
              <stop offset="40%"  stopColor="#D97706" stopOpacity={phase >= 2 ? 0.08 : 0} />
              <stop offset="100%" stopColor="#DC2626" stopOpacity="0" />
            </radialGradient>
            {/* PCI exposure pulse — only at breach phase */}
            <radialGradient id="pci-pulse" cx="78%" cy="64%" r="22%">
              <stop offset="0%"   stopColor="#DC2626" stopOpacity={phase >= 4 ? 0.28 : 0} />
              <stop offset="100%" stopColor="#DC2626" stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect width="100%" height="100%" fill="url(#dark-grid)" opacity="0.4" />

          {/* Contamination spread — phase-driven blast radius */}
          <motion.rect
            x="0" y="0"
            width={VIEW_W} height={VIEW_H}
            fill="url(#contamination-spread)"
            initial={false}
            animate={{ opacity: phase >= 2 ? 1 : 0 }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
            data-testid="contamination-overlay"
            style={{ pointerEvents: 'none' }}
          />

          {/* PCI exposure pulse — sine wave at breach phase */}
          <motion.rect
            x="0" y="0"
            width={VIEW_W} height={VIEW_H}
            fill="url(#pci-pulse)"
            initial={false}
            animate={{ opacity: phase >= 4 ? [0.6, 1, 0.6] : 0 }}
            transition={
              phase >= 4
                ? { duration: 1.8, repeat: Infinity, ease: 'easeInOut' }
                : { duration: 0.4 }
            }
            data-testid="pci-pulse-overlay"
            style={{ pointerEvents: 'none' }}
          />

          {/* Edges — phase-aware */}
          {EDGES.map((e, i) => {
            const s = nodeMap[e.source];
            const t = nodeMap[e.target];
            const sCenter = nodeCenter(s);
            const tCenter = nodeCenter(t);
            const sx = sCenter.x + (tCenter.x > sCenter.x ? s.w / 2 : -s.w / 2);
            const tx = tCenter.x + (tCenter.x > sCenter.x ? -t.w / 2 : t.w / 2);
            const useHorizontal = Math.abs(tCenter.x - sCenter.x) > Math.abs(tCenter.y - sCenter.y);
            const sPt = useHorizontal ? { x: sx, y: sCenter.y } : { x: sCenter.x, y: sCenter.y + (tCenter.y > sCenter.y ? NODE_H / 2 : -NODE_H / 2) };
            const tPt = useHorizontal ? { x: tx, y: tCenter.y } : { x: tCenter.x, y: tCenter.y + (tCenter.y > sCenter.y ? -NODE_H / 2 : NODE_H / 2) };
            const path = bezierPath(sPt, tPt);
            const phaseEdgeState = currentPhase.edges[e.id] || 'hidden';
            const eState = EDGE_STATE_STYLE[phaseEdgeState];
            const tone = TONE[e.tone];
            const stroke = tone.stroke;
            const sw = e.tone === 'critical' ? 2.25 : 1.75;
            const isHovered = hoverEdge === e.id;
            const dim = (hoverNode && !connectedFromHover.has(e.id)) || (hoverEdge && !isHovered);
            const isHidden = phaseEdgeState === 'hidden';
            const computedOpacity = isHidden ? 0 : (dim ? 0.20 : eState.opacity);
            const mid = { x: (sPt.x + tPt.x) / 2, y: (sPt.y + tPt.y) / 2 };

            return (
              <g
                key={e.id}
                data-testid={`graph-full-edge-${e.id}`}
                onMouseEnter={() => !isHidden && setHoverEdge(e.id)}
                onMouseLeave={() => setHoverEdge(null)}
                style={{ cursor: isHidden ? 'default' : 'help', pointerEvents: isHidden ? 'none' : 'auto' }}
              >
                <motion.path
                  d={path}
                  fill="none"
                  stroke={stroke}
                  strokeWidth={isHovered ? sw + 1 : sw}
                  markerEnd={isHidden ? undefined : (e.tone === 'critical' ? 'url(#arrow-critical-full)' : 'url(#arrow-warning-full)')}
                  initial={false}
                  animate={{
                    strokeDasharray: eState.dashed ? '6 4' : '0',
                    opacity: computedOpacity,
                  }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  style={{ filter: e.tone === 'critical' && !isHidden ? 'drop-shadow(0 0 4px rgba(248,113,113,0.4))' : undefined }}
                />

                {eState.animated && !isHidden && (
                  <motion.circle
                    r="4.5"
                    fill={stroke}
                    style={{ filter: `drop-shadow(0 0 6px ${stroke})` }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: dim ? 0.2 : 1 }}
                    transition={{ duration: 0.4 }}
                  >
                    <animateMotion dur="2.4s" repeatCount="indefinite" path={path} rotate="auto" />
                  </motion.circle>
                )}

                {!isHidden && (
                  <foreignObject x={mid.x - 130} y={mid.y - 14} width="260" height="28" style={{ overflow: 'visible', pointerEvents: 'none' }}>
                    <div className="flex justify-center" style={{ opacity: computedOpacity }}>
                      <span
                        className="inline-block bg-slate-800 border border-slate-700 rounded px-2 py-0.5 shadow-md text-slate-300"
                        style={{ fontFamily: 'ui-monospace, "JetBrains Mono", monospace', fontSize: 10, whiteSpace: 'nowrap' }}
                      >
                        {e.label}
                      </span>
                    </div>
                  </foreignObject>
                )}

                {isHovered && (
                  <foreignObject x={mid.x - 150} y={mid.y - 64} width="300" height="48" style={{ overflow: 'visible', pointerEvents: 'none' }}>
                    <div className="flex justify-center">
                      <div className="bg-slate-900 text-white text-[11px] rounded-md px-2.5 py-1.5 shadow-xl border border-slate-700">
                        <div className="font-medium">{e.label}</div>
                        <div className="text-slate-400 text-[10px] font-mono mt-0.5">{e.evt}</div>
                      </div>
                    </div>
                  </foreignObject>
                )}
              </g>
            );
          })}

          {/* Nodes — phase-aware */}
          {NODES_FULL.map((n, i) => {
            const phaseTone = currentPhase.nodes[n.id] || n.tone;
            const tone = TONE[phaseTone];
            const Icon = NODE_KIND_ICON[n.kind];
            const isHovered = hoverNode === n.id;
            const dimmed = (hoverNode && hoverNode !== n.id);
            const onClick = () => navigate(NODE_ROUTES[n.id] ?? '/lens');
            return (
              <g
                key={n.id}
                onMouseEnter={() => setHoverNode(n.id)}
                onMouseLeave={() => setHoverNode(null)}
                onClick={onClick}
                onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onClick(); } }}
                tabIndex={0}
                role="button"
                data-testid={`graph-full-node-${n.id}`}
                style={{ cursor: 'pointer' }}
              >
                <motion.g
                  initial={false}
                  animate={{ opacity: dimmed ? 0.45 : 1, scale: 1 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  style={{ transformOrigin: `${n.x + n.w / 2}px ${n.y + NODE_H / 2}px` }}
                >
                  {(phaseTone === 'critical' || phaseTone === 'warning' || phaseTone === 'lens') && (
                    <motion.rect
                      x={n.x - 4} y={n.y - 4}
                      width={n.w + 8} height={NODE_H + 8}
                      rx="14"
                      initial={false}
                      animate={{ fill: tone.stroke, opacity: isHovered ? 0.30 : 0.18 }}
                      transition={{ duration: 0.5 }}
                      filter="blur(10px)"
                    />
                  )}
                  <motion.rect
                    x={n.x} y={n.y}
                    width={n.w} height={NODE_H}
                    rx="10"
                    fill="#1E293B"
                    initial={false}
                    animate={{ stroke: tone.stroke, strokeWidth: isHovered ? 3 : 2 }}
                    transition={{ duration: 0.5 }}
                  />
                  <motion.rect
                    x={n.x} y={n.y} width={36} height={NODE_H} rx="10"
                    initial={false}
                    animate={{ fill: tone.fill, opacity: 0.7 }}
                    transition={{ duration: 0.5 }}
                  />
                  <foreignObject x={n.x + 8} y={n.y + 18} width="24" height="28" style={{ pointerEvents: 'none' }}>
                    <Icon size={20} color={tone.icon} strokeWidth={2} />
                  </foreignObject>
                  <text x={n.x + 48} y={n.y + 26} fontSize="12" fontWeight="600" fill="#F8FAFC" fontFamily="Inter, ui-sans-serif">
                    {n.label}
                  </text>
                  <text x={n.x + 48} y={n.y + 46} fontSize="10.5" fill="#94A3B8" fontFamily="ui-monospace, 'JetBrains Mono', monospace">
                    {n.sublabel}
                  </text>
                </motion.g>

                {isHovered && (
                  <foreignObject x={n.x + n.w / 2 - 120} y={n.y - 38} width="240" height="34" style={{ overflow: 'visible', pointerEvents: 'none' }}>
                    <div className="flex justify-center">
                      <div className="bg-slate-900 text-white text-[11px] rounded-md px-2.5 py-1 shadow-lg border border-slate-700 inline-flex items-center gap-1.5">
                        <span className="font-mono text-slate-400 text-[10px]">{n.sublabel}</span>
                        <span className="text-slate-600">·</span>
                        <span className="text-white">Click to investigate</span>
                        <ExternalLink className="w-3 h-3 text-apex-lens" />
                      </div>
                    </div>
                  </foreignObject>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Temporal scrubber — phase navigation */}
      <div className="hidden md:block mt-5 border-t border-slate-700 pt-4 shrink-0" data-testid="temporal-scrubber">
        <div className="flex items-center justify-between mb-3 gap-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Governance state at</div>
            <motion.div
              key={currentPhase.date}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sm font-bold text-white font-mono mt-0.5"
            >
              {currentPhase.date}
            </motion.div>
          </div>
          <div className="text-right min-w-0 max-w-md">
            <motion.div
              key={currentPhase.label}
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-sm font-semibold text-white"
            >
              {currentPhase.label}
            </motion.div>
            <motion.div
              key={currentPhase.sub}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-[11px] text-slate-400 font-mono mt-0.5"
            >
              {currentPhase.sub}
            </motion.div>
          </div>
        </div>

        <div className="flex items-stretch gap-2">
          {PHASES.map((p, i) => (
            <button
              key={p.id}
              onClick={() => jumpToPhase(i)}
              data-testid={`phase-btn-${i}`}
              className={`flex-1 py-2 px-1 rounded-lg text-[10px] font-mono transition-all border ${
                i === phase
                  ? 'bg-apex-lens/20 border-apex-lens/60 text-apex-lens font-semibold'
                  : i < phase
                  ? 'bg-slate-700/40 border-slate-600 text-slate-400 hover:border-slate-500'
                  : 'bg-slate-800/40 border-slate-700 text-slate-600 hover:border-slate-600'
              }`}
            >
              <div className="text-center leading-tight">
                <div className={`w-1.5 h-1.5 rounded-full mx-auto mb-1 ${
                  i < phase ? 'bg-slate-400' : i === phase ? 'bg-apex-lens' : 'bg-slate-600'
                }`} />
                {p.label}
              </div>
            </button>
          ))}
        </div>

        <div className="text-[9px] font-mono text-slate-600 text-center mt-2" data-testid="kb-hint">
          ← → arrow keys · Space to replay
        </div>

        <div className="flex items-center justify-between mt-3">
          <button
            onClick={replayTimeline}
            data-testid="replay-timeline"
            className="text-[11px] font-semibold text-apex-lens hover:text-teal-300 transition-colors inline-flex items-center gap-1.5"
          >
            <Play className="w-3 h-3" />
            Replay timeline
          </button>
          <span className="text-[10px] font-mono text-slate-500">
            Phase {phase + 1} of {PHASES.length} · {currentPhase.date}
          </span>
        </div>
      </div>

      {/* Mobile fallback — vertical chain on dark */}
      <div className="md:hidden">
        <ol className="relative border-l-2 border-dashed border-slate-700 ml-3 space-y-4 pl-5 py-2">
          {[
            { label: 'DEV ACCOUNT',                  sub: 'stratum-dev-sandbox · source of contamination',         tone: 'neutral'  },
            { label: 'IAM trust path created',       sub: 'Oct 3, 2025 · no approved baseline',                    tone: 'warning'  },
            { label: 'Dev image pushed to ECR',      sub: 'Nov 28, 2025 · developer-console-access',               tone: 'warning'  },
            { label: 'Image deployed to production', sub: 'Feb 14, 2026 · no pipeline event',                      tone: 'critical' },
            { label: 'Trust path traversed',         sub: 'Apr 14, 2026 02:17 UTC · PCI data accessed',            tone: 'critical' },
            { label: 'Remediation pending',          sub: '4 actions · $199K debt reduction',                      tone: 'lens'     },
          ].map((s, i) => (
            <li key={i} className="ml-2">
              <span className="absolute -left-[7px] mt-1 w-3 h-3 rounded-full ring-4 ring-apex-shell" style={{ background: TONE[s.tone].stroke }} />
              <div className="text-[12px] font-semibold text-white">{s.label}</div>
              <div className="text-[11px] text-slate-400 font-mono mt-0.5">{s.sub}</div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
